import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import LogoIconDecoration from '../components/LogoIconDecoration';

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    async function loadServices() {
      const data = await fetchCollection('services');
      setServices(data);
    }
    loadServices();
  }, []);

  return (
    <div className="pt-[73px]">
      {/* Header Section */}
      <section className="bg-primary text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/img-0227.jpg"
            alt="Floatels Interior"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 text-center space-y-4">
          <span className="text-accent text-xs font-bold tracking-widest uppercase">Our Expertise</span>
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold tracking-tight text-white">
            Tailored Accommodation & Logistics Solutions
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
            We deliver hotel-grade accommodations and support services directly to your specified locations, ensuring comfort, safety, and efficiency.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white relative overflow-hidden">
        <LogoIconDecoration className="w-[600px] h-[600px] opacity-[0.015] -right-24 top-12 rotate-12" />
        <LogoIconDecoration className="w-[500px] h-[500px] opacity-[0.015] -left-20 bottom-12 -rotate-12" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 relative z-10 space-y-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div 
                key={service.id} 
                className={`bg-slate-50/50 rounded-2xl border border-slate-100/80 overflow-hidden flex flex-col justify-between hover:bg-slate-50 hover:border-accent/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${
                  idx === services.length - 1 && services.length % 2 !== 0 ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
              >
                <div>
                  {/* Image */}
                  <div className="h-64 overflow-hidden relative">
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${service.image[0]}`}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4 h-12 w-12 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center text-primary shadow-sm">
                      <i className={`fa-regular ${service.icon} text-xl`} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8 space-y-4">
                    <h3 className="text-2xl font-bold text-primary">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed line-clamp-4">
                      {service.summary}
                    </p>
                  </div>
                </div>

                <div className="p-8 pt-0">
                  <Link
                    to={`/services/${service.slug.en}`}
                    className="inline-flex items-center gap-2 py-3 px-6 rounded-xl bg-slate-50 text-primary font-semibold hover:bg-accent hover:text-white transition-all-300 w-full justify-center border border-slate-100"
                  >
                    <span>Learn More</span>
                    <i className="fa-regular fa-arrow-right text-xs" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Integrated Solution Banner */}
          <div className="bg-primary text-white rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-xl">
            <div className="relative z-10 max-w-3xl space-y-4">
              <span className="inline-flex items-center gap-2 text-xs font-bold text-accent uppercase tracking-widest bg-white/10 px-3 py-1 rounded-full">
                <i className="fa-regular fa-badge-check" />
                Integrated Turnkey Services
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                En samlet løsning med professionel drift og høj gæsteoplevelse
              </h2>
              <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                Vores ydelser inden for Catering, Housekeeping og Laundry er en fuldt integreret del af vores samlede løsninger. Vi er ISO 9001 og ISO 22000 certificerede, hvilket garanterer dokumenterede processer, fødevaresikkerhed og en konsekvent høj standard for både service og gæsteoplevelse.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="mx-auto max-w-5xl px-6 text-center space-y-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-primary">
            Need a Bespoke Accommodation Setup?
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Every project has unique requirements. Contact our specialists today to discuss how we can customize our solutions to match your timeline, capacity, and budget.
          </p>
          <div className="pt-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 py-4 px-8 rounded-xl bg-primary text-white font-semibold hover:bg-primary-light transition-all-300 shadow-sm"
            >
              <span>Get in Touch</span>
              <i className="fa-regular fa-paper-plane text-sm" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
